import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Represents a single segment in the progress bar.
 */
export interface ProgressSegment {
  /** Display label, e.g. 'Passed', 'Failed' */
  label: string;
  /** Percentage (0–100). All segments should sum to ≤ 100. */
  percentage: number;
  /** CSS colour string, e.g. '#22c55e' or 'var(--green)' */
  color: string;
}

export interface ExecutionCounts {
  passed: number;
  failed: number;
  executing: number;
  waiting: number;
}

@Component({
  selector: 'app-multi-segment-progress-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multi-segment-progress-bar.component.html',
  styleUrl: './multi-segment-progress-bar.component.scss'
})
export class MultiSegmentProgressBarComponent implements OnChanges {
  /** Segments to render in the bar. Percentages should sum to ≤ 100. */
  @Input() segments: ProgressSegment[] = [];

  /** Raw execution counts (alternative to segments) */
  @Input() counts?: ExecutionCounts;

  /** Total count for percentage calculation (required when using counts) */
  @Input() total?: number;

  /** Optional label shown top-right, e.g. "55 of 85 completed" */
  @Input() completedLabel: string = '';

  /** Height of the bar in pixels. Defaults to 20. */
  @Input() barHeight: number = 20;

  /** Border radius of the whole bar. Defaults to 50 (pill shape). */
  @Input() borderRadius: number = 50;

  /** Segments filtered to only those with percentage > 0 */
  visibleSegments: ProgressSegment[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // If counts are provided, calculate segments from them
    if (this.counts && this.total && this.total > 0) {
      this.segments = this.calculateSegmentsFromCounts(this.counts, this.total);
    }

    if (changes['segments'] || changes['counts'] || changes['total']) {
      this.visibleSegments = (this.segments || []).filter(s => s.percentage > 0);
    }
  }

  /**
   * Calculate segment percentages from raw counts
   */
  private calculateSegmentsFromCounts(counts: ExecutionCounts, total: number): ProgressSegment[] {
    const toPercentage = (count: number) => Math.round((count / total) * 100);

    return [
      { label: 'Passed', percentage: toPercentage(counts.passed || 0), color: '#02F576' },
      { label: 'Failed', percentage: toPercentage(counts.failed || 0), color: '#FC404A' },
      { label: 'Executing', percentage: toPercentage(counts.executing || 0), color: '#DED6FF' },
      { label: 'Waiting', percentage: toPercentage(counts.waiting || 0), color: '#E8E8E9' }
    ];
  }

  /** Returns the CSS flex value for a segment based on its percentage */
  getSegmentStyle(segment: ProgressSegment): Record<string, string> {
    return {
      flex: String(segment.percentage),
      background: segment.color,
    };
  }

  /** Remaining % not covered by any segment — rendered as the empty/grey track */
  get remainingPercentage(): number {
    const total = this.visibleSegments.reduce((sum, s) => sum + s.percentage, 0);
    return Math.max(0, 100 - total);
  }
}
